import { EntityNotFound } from '@common/EntityNotFound';
import { CodeGenerationContextProvider, useCodeGenerationState } from '@common/git/CodeGeneration/context';
import { usePipelineStatus } from '@common/git/CodeGeneration/hook';
import { GitInfo } from '@common/git/GitInfo';
import { ProjectContextProvider } from '@common/git/Project/context';
import { useProjectState } from '@common/git/Project/hooks';
import { PageHeader } from '@common/Header/PageHeader';
import { PipelineDetailQuery } from '@common/queries/pipeline';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { titleCase } from '@common/utils/metadataUtils';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Button, Centered, Container, Spinner, Stack, StackItem, TabPane, Tabs } from '@prophecy/ui-v3';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { ResultOf } from 'gql.tada';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ExecutionServiceProvider } from '../../../context/execution';
import { useMixPanel } from '../../../context/mixpanel/context';
import { useExecutionServiceInstance } from '../../../data/executionHooks';
import { useGraphQlQuery } from '../../../data/util';
import { AttachClusterWithFabric } from '../../Workflow/AttachFabric/AttachClusterWithFabric';
import { ComponentRunContext, useRunDataContext } from '../../Workflow/AttachFabric/componentRunhook';
import { ClonePipeline } from '../../Workflow/ClonePipeline';
import { InfoEntity } from '../components/EntityInfo';
import PipelineReadWriteDatasets from '../components/PipelineReadWriteDatasets';
import PipelineRelations from '../components/PipelineRelations';
import PipelineRuns from '../components/PipelineRuns';
import { PipelineInfoReadSchema, PipelineInfoSchema, PipelinePageLayout } from '../config/pipeline';
import { Footer } from '../config/styled';
import { getMetadataDefaultCrumbs } from './MetadataDetailHeader';
import { CodeTab } from './ProjectDetail/CodeTab';

const StyledInfoEntity = styled(InfoEntity)`
  height: auto;
  flex: 1;
` as typeof InfoEntity;

const StyledStackItem = styled(StackItem)`
  width: 100%;
`;

export default function Page() {
  const parentEntity = Entity.Pipeline;
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.Pipeline.tab.url>>();

  const uid = decodeURIComponent(params.uid as string);
  const tab = params.tab as string;
  const variables = { uid };

  const { data, isLoading } = useGraphQlQuery(
    [parentEntity, parentEntity + 'Detail', variables],
    PipelineDetailQuery,
    undefined,
    variables
  );

  const item = data?.Pipeline;

  if (isLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  if (!item) return <EntityNotFound uid={uid} entity={Entity.Pipeline} projectContext={true} />;
  return (
    <ProjectContextProvider projectId={item?.project?._id}>
      <CodeGenerationContextProvider>
        <PipelineDetail uid={uid} pipeline={item} tab={tab} />
      </CodeGenerationContextProvider>
    </ProjectContextProvider>
  );
}

function PipelineDetail({
  uid,
  pipeline,
  tab
}: {
  uid: string;
  pipeline: NonNullable<ResultOf<typeof PipelineDetailQuery>['Pipeline']>;
  tab: string;
}) {
  const parentEntity = Entity.Pipeline;

  const [isClone, toggleClone] = useState(false);

  const [activeTab, setActiveTab] = useState(titleCase(tab) || PipelinePageLayout.firstTab);

  const executionService = useExecutionServiceInstance();

  const { projectName, projectId, currentBranch, isMainBranch } = useProjectState();

  const {
    actions: { triggerRebuildCall }
  } = useCodeGenerationState();

  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents.common.metadata.entityOpen, {
      uid,
      name: pipeline.name,
      entity: parentEntity,
      url: window.location.href
    });
  }, [track, pipeline.name, parentEntity, uid]);

  const changeTab = (tab: string) => {
    setActiveTab(tab);
    track(EntityEvents.common.metadata.tabChange, {
      uid,
      tab,
      name: pipeline.name,
      entity: parentEntity,
      url: window.location.href
    });
  };

  const [isSessionUsed, setSessionUsed] = useState(false);
  const readOnly = isMainBranch;
  const { isCodeGenerationInProgress, codeGenStatus } = usePipelineStatus(uid as string, true);
  const getTabDetail = (title: string) => {
    if (title === 'Info') {
      return (
        <Stack align='space-between' height='100%'>
          {isClone && (
            <ClonePipeline
              branch={currentBranch as string}
              sourceProjectId={projectId as string}
              targetProjectId={projectId as string}
              onClose={() => toggleClone(false)}
              pipelinePath={pipeline.path as string}
              isConfigured={false}
            />
          )}
          <Stack grow='1' gap={theme.spaces.x40}>
            <Stack direction='horizontal' align='space-between' width='100%' gap={theme.spaces.x24}>
              <StyledInfoEntity
                hideFooter={true}
                branch={currentBranch}
                hasCodegenContext={false}
                triggerRebuildCall={triggerRebuildCall}
                readOnly={readOnly}
                schema={PipelineInfoSchema(codeGenStatus, isCodeGenerationInProgress)}
                variables={{ uid }}
                projectId={projectId as string}
              />
              <Button
                tooltip={readOnly ? 'Cloning is not allowed on main branch as it is read-only' : ''}
                onClick={() => toggleClone(true)}
                variant='secondaryGray'
                disabled={readOnly}
                data-test-id='pipeline-detail-duplicate-button'>
                Duplicate
              </Button>
            </Stack>
            <Stack grow='1' shrink='1'>
              <PipelineReadWriteDatasets uid={uid} entity={parentEntity} />
            </Stack>
          </Stack>
          <StyledInfoEntity
            readOnly={readOnly}
            hasCodegenContext={false}
            schema={PipelineInfoReadSchema(uid)}
            variables={{ uid }}
            projectId={projectId as string}
          />
        </Stack>
      );
    } else if (title === 'Code') {
      return projectId && <CodeTab projectId={projectId} uri={`/${pipeline.path}`} />;
    } else if (title === 'Relations') {
      return projectId && <PipelineRelations projectId={projectId} entity={parentEntity} uid={uid} />;
    } else if (title === 'Runs') {
      return <PipelineRuns isSessionUsed={isSessionUsed} uid={uid} entity={parentEntity} />;
    }
  };
  return (
    <ExecutionServiceProvider value={executionService}>
      <ComponentRunContext.Provider value={useRunDataContext()}>
        <Stack width='100%' height='100%' align='space-between' alignY='center'>
          <StyledStackItem grow='1'>
            <Stack width='100%' height='100%' align='center' alignY='center'>
              <DocumentTitle title={`Pipeline:${pipeline.name || 'Summary'}`} />
              <PageHeader crumbs={getMetadataDefaultCrumbs(Entity.Pipeline, projectName, projectId, pipeline)} />
              <Container>
                <Tabs
                  activeKey={activeTab}
                  onChange={(tab) => changeTab(tab)}
                  tabBarExtraContent={{
                    right: (
                      <AttachClusterWithFabric
                        projectId={projectId}
                        setSessionUsed={() => setSessionUsed(true)}
                        hide={activeTab !== 'Runs'}
                      />
                    )
                  }}>
                  {PipelinePageLayout.tabs.map((v) => {
                    const title = v.title;
                    return (
                      <TabPane
                        key={title}
                        tab={title}
                        elementType='link'
                        to={Private_Routes.Pipeline.tab.getUrl({ uid, tab: title.toLowerCase() })}>
                        {getTabDetail(title)}
                      </TabPane>
                    );
                  })}
                </Tabs>
              </Container>
            </Stack>
          </StyledStackItem>
          <Footer>{projectId ? <GitInfo projectId={projectId} /> : null}</Footer>
        </Stack>
      </ComponentRunContext.Provider>
    </ExecutionServiceProvider>
  );
}
